import React from 'react';
import { motion } from 'framer-motion';
import Card from './Card';
import { H3, Body } from './Typography';
import Badge from './Badge';

interface StatisticsCardProps {
  title: string;
  value: number | string;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
    period: string;
  };
  icon?: React.ComponentType<{ className?: string }>;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  className?: string;
}

const StatisticsCard: React.FC<StatisticsCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  color = 'primary',
  className = '',
}) => {
  const getColorClasses = (color: string) => {
    switch (color) {
      case 'primary':
        return 'text-primary-600 bg-primary-50';
      case 'secondary':
        return 'text-secondary-600 bg-secondary-50';
      case 'success':
        return 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'error':
        return 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20';
      case 'info':
        return 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20';
      default:
        return 'text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-800';
    }
  };

  const getChangeColor = (type: string) => {
    switch (type) {
      case 'increase':
        return 'text-green-600';
      case 'decrease':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={className}
    >
      <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Body className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              {title}
            </Body>
            <H3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {value}
            </H3>
            {change && (
              <div className="flex items-center">
                <span className={`text-sm font-medium ${getChangeColor(change.type)}`}>
                  {change.type === 'increase' ? '+' : change.type === 'decrease' ? '-' : ''}
                  {Math.round(Math.abs(change.value))}%
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                  {change.period}
                </span>
              </div>
            )}
          </div>
          
          {Icon && (
            <div className={`p-3 rounded-full ${getColorClasses(color)}`}>
              <Icon className="w-6 h-6" />
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export { StatisticsCard };
export default StatisticsCard;
